import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { Loader2, ShieldAlert, Wallet } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Lands the admin "View Treasurer" handoff. Reads a one-shot custom token
 * from the URL hash, exchanges it for a Firebase session via
 * `signInWithCustomToken`, and forwards the user to the requested unit's
 * dashboard.
 *
 * Tokens are read from the URL hash (not the query string) so they never
 * reach the server access logs and so the browser back button cannot
 * replay them — `Navigate` calls below use `replace: true` for the same
 * reason.
 */
export default function AuthHandoffPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const ranRef = useRef(false);
  const [signedIn, setSignedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () => parseHandoffParams(location.hash || location.search),
    [location.hash, location.search],
  );

  const targetPath = params.returnTo && params.returnTo.startsWith('/')
    ? params.returnTo
    : params.unitId
      ? `/u/${params.unitId}/dashboard`
      : '/units';

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!params.token || !params.unitId) {
      setError('Missing handoff token. Please return to the admin panel and try again.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // If a different user is already signed in on this device, drop
        // their session first so the new admin handoff replaces it.
        if (auth.currentUser) {
          await signOut(auth);
        }
        await signInWithCustomToken(auth, params.token!);
        if (cancelled) return;
        setSignedIn(true);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setError(`Handoff failed: ${message}`);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params.token, params.unitId]);

  // Wait for the shared AuthContext to actually reflect the signed-in
  // user before navigating into the protected `/u/:unitId` area —
  // otherwise `RequireAuth` can briefly observe `currentUser === null`
  // and bounce the admin to /login.
  useEffect(() => {
    if (signedIn && currentUser) {
      navigate(targetPath, { replace: true });
    }
  }, [signedIn, currentUser, navigate, targetPath]);

  if (!params.token && !error) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 px-4">
      <div className="w-full max-w-md rounded-3xl border border-emerald-100 bg-white/90 p-8 text-center shadow-xl shadow-emerald-900/10 backdrop-blur">
        <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30">
          <Wallet className="h-6 w-6 text-white" aria-hidden />
        </div>
        {error ? (
          <>
            <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <ShieldAlert className="h-5 w-5" aria-hidden />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">Cannot complete handoff</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-6 inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Go to login
            </button>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-slate-900">Signing you in…</h1>
            <p className="mt-2 text-sm text-slate-600">
              Just a moment while we set up the treasurer dashboard.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-emerald-700">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              <span className="text-sm font-medium">Verifying admin handoff…</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface HandoffParams {
  token: string | null;
  unitId: string | null;
  returnTo: string | null;
}

/**
 * Accepts either `#token=...&unitId=...` or `?token=...&unitId=...`.
 * Hash params are preferred (don't hit server logs) but query strings are
 * still supported because some link previews drop the hash fragment.
 */
function parseHandoffParams(input: string): HandoffParams {
  if (!input) return { token: null, unitId: null, returnTo: null };
  const cleaned = input.startsWith('#') || input.startsWith('?') ? input.slice(1) : input;
  const params = new URLSearchParams(cleaned);
  return {
    token: params.get('token'),
    unitId: params.get('unitId'),
    returnTo: params.get('returnTo'),
  };
}
